import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Users,
  Eye,
  MessageSquare,
  TrendingUp,
  Loader2,
  Monitor,
  Smartphone,
  Tablet,
  Globe,
  ArrowRight,
  Calendar,
  ExternalLink,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { ContactRequestsList } from '@/components/admin/ContactRequestsList';
import { CleanupTestDataButton } from '@/components/admin/CleanupTestDataButton';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  Cell,
} from 'recharts';
import { PageMeta } from '@/components/PageMeta';
import { formatApiError } from '@/components/ProtectedRoute';
import { API } from '@/lib/api';


const RANGES = [
  { days: 7, label: '7 JOURS' },
  { days: 30, label: '30 JOURS' },
  { days: 90, label: '90 JOURS' },
];

const DEVICE_ICON = { desktop: Monitor, mobile: Smartphone, tablet: Tablet };
const DEVICE_COLORS = { desktop: '#0D4F34', mobile: '#22C17D', tablet: '#1E7A4F', unknown: '#94a3b8' };

const formatShortDate = (iso) => {
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
};

const formatDateTime = (iso) => {
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
};

function StatCard({ icon: Icon, label, value, sublabel, accent = 'primary' }) {
  return (
    <Card className="p-6 rounded-2xl border-border hover:border-primary/30 transition-all" data-testid={`stat-card-${label}`}>
      <div className="flex items-start justify-between mb-3">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${accent === 'primary' ? 'bg-primary/10' : 'bg-secondary/20'}`}>
          <Icon className={`h-5 w-5 ${accent === 'primary' ? 'text-primary' : 'text-secondary-foreground'}`} strokeWidth={2.2} />
        </div>
      </div>
      <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">{label}</div>
      <div className="font-display text-4xl text-dark leading-none">{value.toLocaleString('fr-FR')}</div>
      {sublabel && <div className="text-xs text-muted-foreground mt-1.5">{sublabel}</div>}
    </Card>
  );
}

export default function AdminAnalyticsPage() {
  const [range, setRange] = useState(30);
  const [summary, setSummary] = useState(null);
  const [timeseries, setTimeseries] = useState(null);
  const [topPages, setTopPages] = useState(null);
  const [referrers, setReferrers] = useState(null);
  const [devices, setDevices] = useState(null);
  const [contacts, setContacts] = useState(null);
  const [events, setEvents] = useState(null);
  const [funnel, setFunnel] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [sum, ts, pages, refs, devs, cts, evs, fun] = await Promise.all([
          axios.get(`${API}/analytics/summary`),
          axios.get(`${API}/analytics/timeseries?days=${range}`),
          axios.get(`${API}/analytics/top-pages?days=${range}&limit=10`),
          axios.get(`${API}/analytics/referrers?days=${range}&limit=8`),
          axios.get(`${API}/analytics/devices?days=${range}`),
          axios.get(`${API}/analytics/recent-contacts?limit=5`),
          axios.get(`${API}/analytics/events?days=${range}`),
          axios.get(`${API}/analytics/funnel?days=${range}`),
        ]);
        setSummary(sum.data);
        setTimeseries(ts.data.map((d) => ({ ...d, dateShort: formatShortDate(d.date) })));
        setTopPages(pages.data);
        setReferrers(refs.data);
        setDevices(devs.data);
        setContacts(cts.data);
        setEvents(evs.data);
        setFunnel(fun.data);
      } catch (err) {
        toast.error(formatApiError(err));
      }
    };
    load();
  }, [range]);

  const isLoading = !summary || !timeseries || !topPages;

  const totalReferrers = referrers ? referrers.reduce((s, r) => s + r.count, 0) : 1;
  const totalDevices = devices ? devices.reduce((s, d) => s + d.count, 0) : 1;

  return (
    <AdminLayout>
      <PageMeta title="Analytics admin" description="Statistiques du site" />

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <span className="text-primary font-semibold text-xs tracking-[0.3em] uppercase">━━ Analytics</span>
          <h1 className="font-heading text-4xl md:text-5xl text-dark mt-2">
            STATISTIQUES DU <span className="text-gradient-primary italic font-display inline-block pr-2">SITE</span>
          </h1>
          <p className="text-muted-foreground text-sm mt-2">
            Données de trafic en temps réel, anonymes et conformes RGPD.
          </p>
        </div>
        <div className="flex gap-1 bg-muted rounded-xl p-1" data-testid="range-selector">
          {RANGES.map((r) => (
            <button
              key={r.days}
              onClick={() => setRange(r.days)}
              className={`px-4 py-2 rounded-lg text-xs font-semibold tracking-wider transition-all ${
                range === r.days ? 'bg-primary text-white shadow' : 'text-muted-foreground hover:text-dark'
              }`}
              data-testid={`range-${r.days}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 text-primary animate-spin" />
        </div>
      ) : (
        <>
          {/* Summary stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard
              icon={Users}
              label="Visiteurs uniques"
              value={range === 7 ? summary.visitors.week : range === 30 ? summary.visitors.month : summary.visitors.total}
              sublabel={`${summary.visitors.today} aujourd'hui`}
            />
            <StatCard
              icon={Eye}
              label="Pages vues"
              value={range === 7 ? summary.pageviews.week : range === 30 ? summary.pageviews.month : summary.pageviews.total}
              sublabel={`${summary.pageviews.today} aujourd'hui`}
            />
            <StatCard
              icon={TrendingUp}
              label="Pages / visiteur"
              value={
                summary.visitors.month
                  ? Number((summary.pageviews.month / summary.visitors.month).toFixed(1))
                  : 0
              }
              sublabel="30 derniers jours"
            />
            <StatCard
              icon={MessageSquare}
              label="Demandes contact"
              value={summary.contacts_30d}
              sublabel="30 derniers jours"
            />
          </div>

          {/* Chart */}
          <Card className="p-6 rounded-2xl mb-8" data-testid="timeseries-chart">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="font-heading text-lg text-dark tracking-wide">TRAFIC QUOTIDIEN</h2>
                <p className="text-xs text-muted-foreground mt-0.5">Visiteurs uniques + pages vues</p>
              </div>
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={timeseries} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="cViews" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0D4F34" stopOpacity={0.35} />
                      <stop offset="95%" stopColor="#0D4F34" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="cVisitors" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#22C17D" stopOpacity={0.35} />
                      <stop offset="95%" stopColor="#22C17D" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="dateShort" stroke="#9ca3af" fontSize={11} tickLine={false} axisLine={false} minTickGap={24} />
                  <YAxis stroke="#9ca3af" fontSize={11} tickLine={false} axisLine={false} width={40} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#0b1a14',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '12px',
                      color: '#fff',
                      fontSize: '12px',
                    }}
                    labelStyle={{ color: '#9ca3af', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px' }}
                  />
                  <Legend wrapperStyle={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px' }} />
                  <Area type="monotone" dataKey="pageviews" name="Pages vues" stroke="#0D4F34" strokeWidth={2} fill="url(#cViews)" />
                  <Area type="monotone" dataKey="visitors" name="Visiteurs" stroke="#22C17D" strokeWidth={2} fill="url(#cVisitors)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>

          {/* 3-col row: top pages, referrers, devices */}
          <div className="grid lg:grid-cols-3 gap-5 mb-8">
            {/* Top pages */}
            <Card className="p-6 rounded-2xl lg:col-span-1" data-testid="top-pages-card">
              <h2 className="font-heading text-lg text-dark tracking-wide mb-5">PAGES POPULAIRES</h2>
              <div className="space-y-3">
                {topPages.length === 0 && <p className="text-sm text-muted-foreground">Aucune donnée</p>}
                {topPages.slice(0, 8).map((p, i) => {
                  const maxCount = topPages[0]?.pageviews || 1;
                  const pct = Math.round((p.pageviews / maxCount) * 100);
                  return (
                    <div key={p.path} data-testid={`top-page-${i}`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <span className="text-[10px] font-mono text-muted-foreground w-5">{i + 1}</span>
                          <code className="text-xs text-dark truncate font-mono">{p.path}</code>
                        </div>
                        <span className="text-xs font-semibold text-primary ml-2">{p.pageviews}</span>
                      </div>
                      <div className="h-1.5 bg-accent rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-primary-glow to-primary rounded-full transition-all"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>

            {/* Referrers */}
            <Card className="p-6 rounded-2xl" data-testid="referrers-card">
              <div className="flex items-center gap-2 mb-5">
                <Globe className="h-4 w-4 text-primary" />
                <h2 className="font-heading text-lg text-dark tracking-wide">SOURCES DE TRAFIC</h2>
              </div>
              <div className="space-y-3">
                {referrers.length === 0 && <p className="text-sm text-muted-foreground">Aucune donnée</p>}
                {referrers.map((r, i) => {
                  const pct = ((r.count / totalReferrers) * 100).toFixed(0);
                  return (
                    <div key={r.source} className="flex items-center gap-3" data-testid={`referrer-${i}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-dark truncate">{r.source}</span>
                          <span className="text-[10px] text-muted-foreground tabular-nums">{pct}%</span>
                        </div>
                        <div className="h-1.5 bg-accent rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary rounded-full"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                      <span className="text-xs font-semibold text-dark tabular-nums w-10 text-right">{r.count}</span>
                    </div>
                  );
                })}
              </div>
            </Card>

            {/* Devices */}
            <Card className="p-6 rounded-2xl" data-testid="devices-card">
              <h2 className="font-heading text-lg text-dark tracking-wide mb-5">APPAREILS</h2>
              {devices.length === 0 ? (
                <p className="text-sm text-muted-foreground">Aucune donnée</p>
              ) : (
                <>
                  <div className="h-36 mb-4">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={devices} layout="vertical" margin={{ top: 0, right: 10, left: 0, bottom: 0 }}>
                        <XAxis type="number" hide />
                        <YAxis type="category" dataKey="device" stroke="#9ca3af" fontSize={11} tickLine={false} axisLine={false} width={70} />
                        <Tooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                        <Bar dataKey="count" radius={[0, 8, 8, 0]}>
                          {devices.map((d) => (
                            <Cell key={d.device} fill={DEVICE_COLORS[d.device] || DEVICE_COLORS.unknown} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="space-y-2">
                    {devices.map((d) => {
                      const Icon = DEVICE_ICON[d.device] || Monitor;
                      const pct = ((d.count / totalDevices) * 100).toFixed(0);
                      return (
                        <div key={d.device} className="flex items-center justify-between text-xs">
                          <div className="flex items-center gap-2 capitalize">
                            <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                            <span className="text-dark">{d.device}</span>
                          </div>
                          <span className="text-muted-foreground tabular-nums">{pct}%</span>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </Card>
          </div>

          {/* Recent contacts — rich list with status update */}
          <Card className="p-6 rounded-2xl" data-testid="recent-contacts-card">
            <ContactRequestsList onlyNew={false} limit={50} title="DEMANDES DE CONTACT" />
          </Card>

          {/* Insights hints */}
          <Card className="p-6 rounded-2xl bg-primary/5 border-primary/20 mt-6">
            <h3 className="font-heading text-lg text-dark mb-3">💡 INSIGHTS</h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              {summary.visitors.week > 0 && (
                <li>• Sur les 7 derniers jours, <strong className="text-dark">{summary.visitors.week}</strong> visiteurs uniques ont consulté <strong className="text-dark">{summary.pageviews.week}</strong> pages.</li>
              )}
              {topPages[0] && (
                <li>• La page la plus consultée est <code className="text-primary text-xs bg-primary/10 px-1.5 py-0.5 rounded">{topPages[0].path}</code> avec {topPages[0].pageviews} vues.</li>
              )}
              {summary.contacts_30d > 0 && (
                <li>• Taux de conversion contact : <strong className="text-dark">{((summary.contacts_30d / Math.max(summary.visitors.month, 1)) * 100).toFixed(1)}%</strong> (contacts / visiteurs sur 30 jours).</li>
              )}
              <li className="text-xs text-muted-foreground/80 pt-2 border-t border-primary/10">
                <Link to="/admin/blog/new" className="text-primary hover:underline">Publier un nouvel article</Link> pour attirer plus de trafic organique.
                <span className="mx-2">·</span>
                <Link to="/admin/pricing" className="text-primary hover:underline">Modifier les tarifs</Link>
                <span className="mx-2">·</span>
                <Link to="/" className="text-primary hover:underline inline-flex items-center gap-1">Voir le site <ArrowRight className="h-3 w-3" /></Link>
              </li>
            </ul>
          </Card>

          {/* ─────── Funnel de conversion ─────── */}
          {funnel && funnel.steps && (
            <Card className="p-6 mt-6" data-testid="funnel-card">
              <h3 className="font-heading text-xl text-dark mb-1">Funnel de conversion</h3>
              <p className="text-xs text-muted-foreground mb-5">
                Parcours des visiteurs sur les {funnel.days} derniers jours. % = proportion par rapport à l'étape la plus haute.
              </p>
              <div className="space-y-2">
                {funnel.steps.map((s, i) => (
                  <div key={i} data-testid={`funnel-step-${i}`} className="flex items-center gap-3">
                    <div className="flex-1 relative h-10 rounded-lg bg-accent/40 overflow-hidden">
                      <div
                        className="absolute inset-y-0 left-0 bg-primary/80 transition-all duration-700"
                        style={{ width: `${s.pct}%` }}
                      />
                      <div className="relative z-10 px-3 h-full flex items-center justify-between text-xs">
                        <span className="font-semibold text-dark truncate max-w-[60%]">{s.name}</span>
                        <span className="font-mono text-dark/80">{s.visitors} visiteurs · {s.pct}%</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* ─────── Top events conversion ─────── */}
          {events && events.length > 0 && (
            <Card className="p-6 mt-6" data-testid="events-card">
              <h3 className="font-heading text-xl text-dark mb-1">Événements de conversion</h3>
              <p className="text-xs text-muted-foreground mb-5">
                Actions trackées sur les {range} derniers jours (clics CTA, formulaires envoyés, clics téléphone/Deciplus).
              </p>
              <div className="divide-y divide-border">
                {events.map((e, i) => (
                  <div key={i} className="flex items-center justify-between py-2.5 text-sm" data-testid={`event-row-${i}`}>
                    <span className="font-mono text-dark">{e.name}</span>
                    <span className="text-muted-foreground">
                      <strong className="text-dark">{e.count}</strong> · {e.unique_visitors} visiteurs uniques
                    </span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* ─────── Maintenance: cleanup ─────── */}
          <div className="mt-6">
            <CleanupTestDataButton />
          </div>
        </>
      )}
    </AdminLayout>
  );
}
